import React, { useState } from "react";
import { Box, Drawer } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { QUIZ } from "@/theme/quizTokens";

const LOGO_SRC =
  "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Logo.png?v=1756962889";

const DESKTOP_LINKS: { label: string; href: string; active?: boolean }[] = [
  { label: "Our Food", href: "/pages/puppies", active: true },
  { label: "How it Works", href: "/pages/how-it-works" },
  { label: "Reviews", href: "/pages/reviews" },
  { label: "Our Story", href: "/pages/our-story" },
];

/**
 * Brown top navigation bar from the Figma redesign.
 * Mobile (81px): logo left + hamburger right.
 * Desktop (96px): nav links left, centred logo, Support / Try Paws / Login right.
 * The site logo (a monochrome brown PNG) is inverted to white for the dark bar.
 */
export function QuizHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  const logo = (
    <Box
      component="img"
      src={LOGO_SRC}
      alt="Paws on Plates"
      sx={{
        height: { xs: 40, md: 48 },
        width: "auto",
        display: "block",
        filter: "brightness(0) invert(1)", // brown logo → white
      }}
    />
  );

  return (
    <Box
      component="nav"
      sx={{
        width: "100%",
        bgcolor: QUIZ.color.brown,
        minHeight: { xs: QUIZ.layout.navMobile, md: QUIZ.layout.navDesktop },
        display: "flex",
        alignItems: "center",
        px: { xs: "20px", md: "38px" },
        boxSizing: "border-box",
      }}
    >
      {/* ---- Mobile: logo + hamburger ---- */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box component="a" href="/" sx={{ display: "block", lineHeight: 0 }}>
          {logo}
        </Box>
        <Box
          role="button"
          aria-label="Menu"
          onClick={() => setMenuOpen(true)}
          sx={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: QUIZ.color.cream,
            cursor: "pointer",
          }}
        >
          <MenuIcon />
        </Box>
      </Box>

      {/* ---- Mobile menu drawer ---- */}
      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: 360,
              bgcolor: QUIZ.color.brown,
              p: "20px",
              boxSizing: "border-box",
            },
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Box
            role="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            sx={{
              width: 46,
              height: 46,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: QUIZ.color.cream,
              cursor: "pointer",
            }}
          >
            <CloseIcon />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px", mt: "18px" }}>
          {[...DESKTOP_LINKS, { label: "Support", href: "/pages/support" }].map((l) => (
            <Box
              key={l.label}
              component="a"
              href={l.href}
              sx={{
                fontFamily: QUIZ.font.body,
                fontWeight: 700,
                fontSize: "20px",
                textTransform: "uppercase",
                color: QUIZ.color.cream,
                textDecoration: "none",
              }}
            >
              {l.label}
            </Box>
          ))}
          <Box
            component="a"
            href="/account"
            sx={{
              border: `2px solid ${QUIZ.color.cream}`,
              color: QUIZ.color.white,
              fontFamily: QUIZ.font.body,
              fontSize: "16px",
              borderRadius: QUIZ.radius.pill,
              px: "26px",
              py: "10px",
              textDecoration: "none",
              textAlign: "center",
              mt: "8px",
            }}
          >
            Login
          </Box>
        </Box>
      </Drawer>

      {/* ---- Desktop: links · logo · actions ---- */}
      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          width: "100%",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: "18px", alignItems: "center" }}>
          {DESKTOP_LINKS.map((l) => (
            <Box
              key={l.label}
              component="a"
              href={l.href}
              sx={{
                fontFamily: QUIZ.font.body,
                fontWeight: l.active ? 700 : 400,
                fontSize: "16px",
                textTransform: "uppercase",
                color: l.active ? QUIZ.color.green : QUIZ.color.cream,
                textDecoration: "none",
                whiteSpace: "nowrap",
                "&:hover": { opacity: 0.85 },
              }}
            >
              {l.label}
            </Box>
          ))}
        </Box>

        <Box component="a" href="/" sx={{ display: "block", lineHeight: 0, justifySelf: "center" }}>
          {logo}
        </Box>

        <Box sx={{ display: "flex", gap: "22px", alignItems: "center", justifySelf: "end" }}>
          <Box
            component="a"
            href="/pages/support"
            sx={{
              fontFamily: QUIZ.font.body,
              fontSize: "16px",
              color: QUIZ.color.cream,
              textDecoration: "none",
              textTransform: "capitalize",
              "&:hover": { opacity: 0.85 },
            }}
          >
            Support
          </Box>
          <Box
            component="a"
            href="/pages/quiz"
            sx={{
              bgcolor: QUIZ.color.blue,
              color: QUIZ.color.dark,
              fontFamily: QUIZ.font.body,
              fontWeight: 700,
              fontSize: "14.77px",
              borderRadius: QUIZ.radius.pill,
              px: "37px",
              py: "10px",
              textDecoration: "none",
              whiteSpace: "nowrap",
              "&:hover": { opacity: 0.9 },
            }}
          >
            Try Paws
          </Box>
          <Box
            component="a"
            href="/account"
            sx={{
              border: `2px solid ${QUIZ.color.cream}`,
              color: QUIZ.color.white,
              fontFamily: QUIZ.font.body,
              fontSize: "16px",
              borderRadius: QUIZ.radius.pill,
              px: "26px",
              py: "8px",
              textDecoration: "none",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            Login
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
